import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useApi } from '../contexts/ApiContext'
import LoadingSpinner from '../components/LoadingSpinner'
import { 
  DollarSign, 
  Users, 
  AlertCircle, 
  MessageSquare,
  UserCheck,
  ArrowRight,
  Activity,
  CheckCircle2,
  Info
} from 'lucide-react'

export default function Dashboard() {
  const { getDashboardStats } = useApi()
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [version, setVersion] = useState(null)

  useEffect(() => {
    loadStats()
    loadVersion()
  }, [])

  const loadStats = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await getDashboardStats()
      setStats(data.data || data)
    } catch (err) {
      console.error('Erro ao carregar estatísticas:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const loadVersion = async () => {
    try {
      const response = await fetch('/version.json')
      const data = await response.json()
      setVersion(data)
    } catch (err) {
      console.error('Erro ao carregar versão:', err)
    }
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" text="Carregando estatísticas..." />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
        <p className="text-gray-600 text-lg mb-4">Erro ao carregar dados</p>
        <p className="text-gray-500 mb-6">{error}</p>
        <button
          onClick={loadStats}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
        >
          Tentar novamente
        </button>
      </div>
    )
  }

  const cards = [
    {
      title: 'Total em Atraso',
      value: formatCurrency(stats?.Valor_Vencido || 0),
      subtitle: `${stats?.Contas_Vencidas || 0} contas vencidas`,
      icon: DollarSign,
      color: 'from-red-500 to-red-600',
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
      trend: '+12%',
      trendUp: false
    },
    {
      title: 'Clientes em Atraso',
      value: stats?.Total_Clientes_Devendo || 0,
      subtitle: 'necessitam atenção',
      icon: Users,
      color: 'from-orange-500 to-orange-600',
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
      trend: '+5',
      trendUp: false
    },
    {
      title: 'Cobrança WhatsApp',
      value: stats?.Contas_30_89_Dias || 0,
      subtitle: 'clientes aguardando',
      icon: MessageSquare,
      color: 'from-yellow-500 to-yellow-600',
      iconBg: 'bg-yellow-100',
      iconColor: 'text-yellow-600',
      trend: formatCurrency(stats?.Valor_30_89_Dias || 0)
    },
    {
      title: 'Cobrança Presencial',
      value: stats?.Contas_90_Mais_Dias || 0,
      subtitle: 'clientes para visitar',
      icon: UserCheck,
      color: 'from-purple-500 to-purple-600',
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600',
      trend: formatCurrency(stats?.Valor_90_Mais_Dias || 0)
    }
  ]

  return (
    <div className="space-y-6">
      {/* Welcome Banner com Versão */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 text-white shadow-xl relative overflow-hidden">
        {/* Badge de Versão - Canto Superior Direito */}
        <Link
          to="/sobre"
          className="absolute top-4 right-4 flex items-center space-x-2 px-4 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full transition-all duration-300 border border-white/20 group"
          title="Ver detalhes da versão e changelog"
        >
          <Info className="w-4 h-4 text-white" />
          <span className="text-sm font-medium text-white">
            v{version?.version || '1.0.0'}
          </span>
          <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
        </Link>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">
              Bem-vindo ao Sistema de Cobrança! 👋
            </h1>
            <p className="text-blue-100 text-lg">
              Gerencie suas cobranças de forma inteligente e automatizada
            </p>
          </div>
          <div className="hidden lg:block">
            <Activity className="w-24 h-24 text-blue-300 opacity-50" />
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {cards.map((card, index) => {
          const Icon = card.icon
          return (
            <div
              key={index}
              className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden group cursor-pointer"
            >
              <div className={`h-2 bg-gradient-to-r ${card.color}`} />
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-lg ${card.iconBg}`}>
                    <Icon className={`w-6 h-6 ${card.iconColor}`} />
                  </div>
                  {card.trend && (
                    <span className={`text-sm font-medium px-2 py-1 rounded-full ${
                      card.trendUp === false 
                        ? 'bg-red-100 text-red-700' 
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {card.trend}
                    </span>
                  )}
                </div>
                
                <p className="text-sm text-gray-600 mb-1">{card.title}</p>
                <p className="text-3xl font-bold text-gray-900 mb-1">
                  {card.value}
                </p>
                <p className="text-sm text-gray-500">{card.subtitle}</p>
              </div>
            </div>
          )
        })}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* WhatsApp Actions */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <MessageSquare className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Cobrança via WhatsApp
                </h3>
                <p className="text-sm text-gray-500">
                  {stats?.Contas_30_89_Dias || 0} clientes aguardando
                </p>
              </div>
            </div>
          </div>
          
          <div className="space-y-3 mb-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-600">Valor total</span>
              <span className="text-sm font-semibold text-gray-900">
                {formatCurrency(stats?.Valor_30_89_Dias || 0)}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-600">Última execução</span>
              <span className="text-sm font-semibold text-gray-900">Hoje, 09:00</span>
            </div>
          </div>

          <Link
            to="/cobranca-whatsapp"
            className="flex items-center justify-center space-x-2 w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium group"
          >
            <span>Gerenciar Cobranças</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        {/* Presencial Actions */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <UserCheck className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Cobrança Presencial
                </h3>
                <p className="text-sm text-gray-500">
                  {stats?.Contas_90_Mais_Dias || 0} clientes para visitar
                </p>
              </div>
            </div>
          </div>
          
          <div className="space-y-3 mb-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-600">Valor total</span>
              <span className="text-sm font-semibold text-gray-900">
                {formatCurrency(stats?.Valor_90_Mais_Dias || 0)}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-600">Lista atualizada</span>
              <span className="text-sm font-semibold text-gray-900">Hoje</span>
            </div>
          </div>

          <Link
            to="/cobranca-presencial"
            className="flex items-center justify-center space-x-2 w-full px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium group"
          >
            <span>Ver Lista de Cobrança</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Atividades Recentes</h3>
          <button className="text-sm text-blue-600 hover:text-blue-700 font-medium">
            Ver todas
          </button>
        </div>

        <div className="space-y-4">
          {[
            // {
            //   icon: MessageSquare,
            //   color: 'bg-green-100 text-green-600',
            //   title: 'Cobrança automática executada',
            //   subtitle: '15 mensagens enviadas via WhatsApp',
            //   time: 'Há 2 horas'
            // },
            {
              icon: CheckCircle2,
              color: 'bg-blue-100 text-blue-600',
              title: 'Cliente realizou pagamento',
              subtitle: 'João Silva - R$ 1.250,00',
              time: 'Há 3 horas'
            },
            {
              icon: AlertCircle,
              color: 'bg-yellow-100 text-yellow-600',
              title: 'Novo cliente em atraso',
              subtitle: 'Maria Santos - 32 dias',
              time: 'Há 5 horas'
            }
          ].map((activity, index) => {
            const Icon = activity.icon
            return (
              <div key={index} className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className={`p-2 rounded-lg ${activity.color}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                  <p className="text-sm text-gray-500">{activity.subtitle}</p>
                </div>
                <span className="text-xs text-gray-400">{activity.time}</span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}